import { TransactionView } from "../../models/view/transaction/transaction.view";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { AccountView } from "../../models/view/account/account.view";
import { TransactionStoreService } from "../../services/store/transaction/transaction.store.service";
import { inject } from "@angular/core";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { PartialTransactionDeleteError } from "../../services/store/transaction/transaction.store.collection";

export const deletingItem = {
  account: "account",
  transaction: "transaction",
};

export class DeleteItem {
  private transactionStore: TransactionStoreService = inject(TransactionStoreService);
  private importStore: ImportStoreService = inject(ImportStoreService);
  private logService: LogService = inject(LogService);
  private i18nService: I18nService = inject(I18nService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private userStore: UserStoreService = inject(UserStoreService);

  item: string = "";

  constructor(item: string) {
    this.item = item;
  }

  async delete(itemView: TransactionView | AccountView): Promise<boolean> {
    let transactions: TransactionView[];

    if (itemView instanceof AccountView) {
      transactions = this.transactionStore.transaction
        .transactionViews()
        .filter((t) => t.accountLink.id === itemView.id);

      if (!(await this.isDeleteConfirmed(transactions))) {
        return false;
      }

      if (!(await this.transactionStore.purgeAccount(itemView, transactions))) {
        return false;
      }
    } else if (itemView instanceof TransactionView) {
      transactions = [itemView];

      if (!(await this.isDeleteConfirmed(transactions))) {
        return false;
      }
      try {
        await this.deleteTransaction(transactions);
      } catch (error) {
        if (error instanceof PartialTransactionDeleteError) {
          // A write after the first commit failed: either the linked-partner repair committed but
          // the transaction delete write failed (transaction NOT deleted), or the transaction
          // delete committed but its source-book cleanup failed (transaction WAS deleted). Both are
          // partial states a retry resolves, so surface the incomplete message rather than the
          // misleading "not deleted", which would imply nothing changed. #4442 / #4504 item 9.
          this.logService.error(
            "Transaction delete partially completed — a follow-up write did not finish: " +
              (error.cause instanceof Error
                ? (error.cause.stack ?? error.cause.message)
                : String(error.cause)),
          );
          await this.handleDeletionError("transactionDeleteIncomplete");
        } else {
          this.logService.info("Something went wrong while deleting transaction");
          await this.handleDeletionError(`${this.item}NotDeleted`);
        }
        return false;
      }
    }

    this.confirmationDialogService.notify(
      NotificationEnum.success,
      "succeeded",
      `${this.item}DeletedSuccess`,
    );

    return true;
  }

  private async handleDeletionError(errorMessage: string) {
    this.confirmationDialogService.notify(NotificationEnum.error, "errorOccurred", errorMessage);
    return false;
  }

  // Errors propagate to the caller so it can tell a PartialTransactionDeleteError (repair kept,
  // delete unfinished) apart from a clean failure and surface the right message. deleteFromVault
  // resolves true or throws — it never returns false — so there is no failure return to check.
  private async deleteTransaction(itemView: TransactionView[]): Promise<void> {
    if (!itemView || !itemView?.length) {
      return;
    }

    await this.transactionStore.transaction.deleteFromVault(itemView);

    // The main delete has committed. `?.` treats an unloaded/cleared sourceTransactionViews() signal
    // (reset to null on clear) as "no source row to clean" rather than a TypeError the caller would
    // misreport as "not deleted". Any real throw from the source cleanup below is a partial (some
    // writes landed, some didn't), surfaced as the incomplete message. #4504 item 9 / #4.
    try {
      // The forward link, confirmed by the back link (srId = tx id, set by every sync origin) or, for
      // old rows without one, the account. The back link still matches a moved transaction (the move never
      // rewrites the source row's acId, #5745); requiring the forward link stops an org-forked copy or a
      // same-id duplicate from deleting a source row another transaction still owns.
      const sourceTransaction = this.importStore.sourceTransaction
        .sourceTransactionViews()
        ?.find(
          (s) =>
            s.id === itemView[0].sourceLink?.id &&
            (s.sourceId === itemView[0].id || s.accountId === itemView[0].accountLink.id),
        );
      if (sourceTransaction) {
        await this.importStore.sourceTransaction.deleteFromVault([sourceTransaction]);
      }
    } catch (error) {
      throw new PartialTransactionDeleteError(error);
    }
  }

  private async isDeleteConfirmed(itemViews?: TransactionView[]) {
    let isConfirmed = false;
    if (itemViews && itemViews?.length > 0) {
      return await this.confirmForDeletion(this.item === deletingItem.account ? itemViews : null);
    }

    isConfirmed = await this.confirmationDialogService.confirmFor(
      ConfirmationEnum[`deleting${this.getI18Name(this.item)}` as keyof typeof ConfirmationEnum],
    );

    return isConfirmed;
  }

  private async confirmForDeletion(items?: TransactionView[]) {
    const message =
      this.item === deletingItem.account
        ? this.i18nService.t("deleteAccountConfirmMessageAllTransactions", items.length.toString())
        : this.i18nService.t(`areYouSureToDelete${this.getI18Name(this.item)}`);
    return await this.confirmationDialogService.confirmFor(
      ConfirmationEnum.deletingAccountWithGeneratedMessage,
      { messageReplacement: message },
    );
  }

  private getI18Name(itemName: string) {
    return itemName.charAt(0).toUpperCase() + itemName.slice(1);
  }
}
